import React from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/joy/Box';
import Button from '@mui/joy/Button';
import Typography from '@mui/joy/Typography';
import Avatar from '@mui/joy/Avatar';
import Dropdown from '@mui/joy/Dropdown';
import MenuButton from '@mui/joy/MenuButton';
import Menu from '@mui/joy/Menu';
import MenuItem from '@mui/joy/MenuItem';
import Divider from '@mui/joy/Divider';
import Chip from '@mui/joy/Chip';

// Icons
import PersonIcon from '@mui/icons-material/Person';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

import { themeColors, shadows } from '../../styles/theme';

export default function ModernHeader({
  selectedWorkspace,
  user,
  onLogout,
  title = "Dashboard"
}) {
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        height: 72,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 4,
        backgroundColor: 'white',
        boxShadow: shadows.sm,
        position: 'sticky',
        top: 0,
        zIndex: 50,
      }}
    >
      {/* Left: Page Title & Context */}
      <Box>
        <Typography level="h4" sx={{ fontWeight: 700, color: themeColors.neutral[900] }}>
          {selectedWorkspace ? selectedWorkspace.name : title}
        </Typography>
        {selectedWorkspace && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 0.5 }}>
            <Typography level="body-sm" sx={{ color: themeColors.neutral[600] }}>
              <Box component="span" sx={{ fontFamily: 'monospace', backgroundColor: themeColors.neutral[100], px: 1, py: 0.25, borderRadius: 0.5 }}>
                /{selectedWorkspace.url_name}
              </Box>
            </Typography>
            <Chip size="sm" variant="soft" color="primary">
              {selectedWorkspace.project_count || 0} flows
            </Chip>
          </Box>
        )}
      </Box>

      {/* Right: Actions & User Menu */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
{/* User Menu */}
        <Dropdown>
          <MenuButton
            variant="plain"
            sx={{
              borderRadius: 2.5,
              minHeight: 44,
              px: 1.5,
              gap: 1.5,
              border: 'none',
              '&:hover': {
                backgroundColor: themeColors.neutral[100],
                boxShadow: shadows.sm,
              },
              transition: 'all 0.2s ease',
            }}
            endDecorator={<KeyboardArrowDownIcon />}
          >
            <Avatar
              size="sm"
              sx={{
                backgroundColor: themeColors.primary[100],
                color: themeColors.primary[600],
                '--Avatar-size': '36px',
              }}
            >
              <PersonIcon />
            </Avatar>
            <Box sx={{ textAlign: 'left', display: { xs: 'none', md: 'block' } }}>
              <Typography level="title-sm" sx={{ fontWeight: 600 }}>
                {user?.email?.split('@')[0] || 'User'}
              </Typography>
              <Typography level="body-xs" sx={{ color: themeColors.neutral[600] }}>
                Creator
              </Typography>
            </Box>
          </MenuButton>

          <Menu
            placement="bottom-end"
            sx={{
              minWidth: 220,
              zIndex: 1400,
              '--List-padding': '8px',
              boxShadow: shadows.xl,
              border: 'none',
              borderRadius: 2.5,
            }}
          >
            <Box sx={{ px: 2, py: 1.5 }}>
              <Typography level="title-sm" sx={{ fontWeight: 600 }}>
                {user?.email || 'User'}
              </Typography>
              <Typography level="body-xs" sx={{ color: themeColors.neutral[600] }}>
                Creator Account
              </Typography>
            </Box>

            <Box sx={{ mx: 2, my: 1, height: '1px', backgroundColor: themeColors.neutral[100] }} />

            <MenuItem
              onClick={() => navigate('/settings')}
              sx={{
                py: 1.5,
                mx: 1,
                borderRadius: 1.5,
                '&:hover': {
                  backgroundColor: themeColors.neutral[100],
                },
                transition: 'all 0.15s ease',
              }}
            >
              <SettingsIcon fontSize="small" />
              <Typography level="body-sm" sx={{ ml: 1.5 }}>Settings</Typography>
            </MenuItem>

            <Box sx={{ mx: 2, my: 1, height: '1px', backgroundColor: themeColors.neutral[100] }} />

            <MenuItem
              onClick={onLogout}
              sx={{
                py: 1.5,
                mx: 1,
                borderRadius: 1.5,
                color: themeColors.danger,
                '&:hover': {
                  backgroundColor: `${themeColors.danger}10`,
                },
                transition: 'all 0.15s ease',
              }}
            >
              <LogoutIcon fontSize="small" />
              <Typography level="body-sm" sx={{ ml: 1.5, fontWeight: 600 }}>Sign Out</Typography>
            </MenuItem>
          </Menu>
        </Dropdown>
      </Box>
    </Box>
  );
}
